import axios, { AxiosResponse, CancelToken } from "axios"
import { AddEmpresaCommandProps } from "./Commands/AddEmpresaCommand"
import { GetAllEmpresasGestionadasVerificadasUsuarioDtoProps } from "./DTOs/GetAllEmpresasGestionadasVerificadasUsuarioDto"
import { GetEmpresaActivaUsuarioDtoProps } from "./DTOs/GetEmpresaActivaUsuarioDto"
import { GetUsuarioLoggedDtoProps } from "./DTOs/GetUsuarioLoggedDto"
import { UsuarioUpdateDtoProps } from "./DTOs/UsuarioUpdateDto"
import { GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable } from "./Schemas/GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable"
import { GetAllEmpresasUsuarioDtoIPagedEnumerableProps } from "./Schemas/GetAllEmpresasUsuarioDtoIPagedEnumerable"
import { UsuarioReadingFiltersDtoIPagedEnumerableProps } from "./Schemas/UsuarioReadingFiltersDtoIPagedEnumerable"
import {
  GetEmpresaActivaUsuarioDtoAdapter,
  empresaReadingFullDtoAdapter,
  getAllEmpresasGestionadasVerificadasUsuarioAdapter,
  getUsuarioByCurrentUserAdapter,
  usuarioReadingFiltersAdapter,
  validacionesPendientesUsuarioAdapter,
} from "./Usuario.adapter"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export interface ValidacionesPendientesUsuarioProps {
  hayCesiones: boolean
  hayFacturas: boolean
  hayPagares: boolean
}
/**
 * @description Obtiene validaciones pendientes para el usuario activo de la empresa.
 * @async
 * @function
 * @returns {Promise<ValidacionesPendientesUsuarioProps>}
 */

export async function getValidacionesPendientesUsuario(
  cancelToken?: CancelToken
): Promise<ValidacionesPendientesUsuarioProps> {
  const { data }: Record<"data", ValidacionesPendientesUsuarioProps> =
    await axios.get("/Usuario/empresa-activa/any-validaciones-pendientes", {
      cancelToken,
    })
  return validacionesPendientesUsuarioAdapter(data)
}

/**
 * Obtiene la lista de empresas para el usuario.
 *
 * @param {boolean} Verified - Indica si debe filtrar por cuentas verificadas.
 * @returns {Promise<GetAllEmpresasUsuarioDtoIPagedEnumerableProps>}
 */

interface UsuarioEmpresasQueryParamsProps {
  Verificada?: boolean
  SortingCriteria?: SortingCriteria
  MaxResultCount?: number
  SkipCount?: number
}
export async function getUsuarioEmpresas(
  queryParams?: UsuarioEmpresasQueryParamsProps
): Promise<GetAllEmpresasUsuarioDtoIPagedEnumerableProps> {
  const {
    data,
  }: Record<"data", GetAllEmpresasUsuarioDtoIPagedEnumerableProps> =
    await axios.get("/Usuario/empresas", {
      params: queryParams,
    })
  return {
    currentPage: data?.currentPage,
    items: data?.items?.map((empresa) => empresaReadingFullDtoAdapter(empresa)),
    totalCount: data?.totalCount,
  }
}

/**
 * Obtiene la empresa activa del usuario.
 *
 * @returns {Promise<GetEmpresaActivaUsuarioDtoProps>}
 */
export async function getUsuarioEmpresaActiva(): Promise<GetEmpresaActivaUsuarioDtoProps> {
  const { data }: Record<"data", GetEmpresaActivaUsuarioDtoProps> =
    await axios.get("/Usuario/empresa-activa")
  return GetEmpresaActivaUsuarioDtoAdapter(data)
}

/**
 * Retrieves all the verified managed companies for a user.
 * @returns {Promise<GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable>}
 */

export async function GetAllEmpresasGestionadasVerificadasUsuario(): Promise<GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable> {
  const {
    data,
  }: Record<
    "data",
    GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable
  > = await axios.get("/Usuario/empresas-gestionadas")
  return {
    currentPage: data?.currentPage,
    items: data.items?.map(
      (empresa: GetAllEmpresasGestionadasVerificadasUsuarioDtoProps) =>
        getAllEmpresasGestionadasVerificadasUsuarioAdapter(empresa)
    ),
    totalCount: data?.totalCount,
  }
}

/**
 * Retrieves the current user's information from the server.
 * @returns A promise that resolves to a UsuarioReadingDtoProps object representing the current user.
 */
export async function getUsuarioByCurrentUser(): Promise<GetUsuarioLoggedDtoProps> {
  const { data }: Record<"data", GetUsuarioLoggedDtoProps> = await axios.get(
    "/Usuario/by-current-user"
  )

  return getUsuarioByCurrentUserAdapter(data)
}

export interface GetUsuarioByFiltersParamsProps {
  DepartamentoId?: string
  Nombre?: string
  SoloResponsable?: boolean
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

/**
 * Retrieves a list of usuarios based on the provided filters.
 *
 * @param queryParams - Optional query parameters for filtering the usuarios.
 * @returns A Promise that resolves to a paged enumerable of usuarios.
 */
export async function getUsuarioByFilters(
  queryParams?: GetUsuarioByFiltersParamsProps
): Promise<UsuarioReadingFiltersDtoIPagedEnumerableProps> {
  const {
    data,
  }: Record<"data", UsuarioReadingFiltersDtoIPagedEnumerableProps> =
    await axios.get("/Usuario/by-filters", {
      params: queryParams,
    })

  return {
    currentPage: data.currentPage,
    items: data.items?.map((usuario) => usuarioReadingFiltersAdapter(usuario)),
    totalCount: data.totalCount,
  }
}

/**
 * Updates a user with the specified ID.
 * @param id - The ID of the user to update.
 * @param requestBody - The updated user data.
 * @returns A Promise that resolves to the AxiosResponse object containing the response data.
 */
export async function usuarioUpdate(
  id: string,
  requestBody: UsuarioUpdateDtoProps
): Promise<AxiosResponse> {
  const response = await axios.put(`/Usuario/${id}`, requestBody)
  return response
}

/**
 * Sends a POST request to add an external company to a user.
 *
 * @param empresa - The command properties required to add the company.
 * @returns A promise that resolves to the Axios response of the POST request.
 */
export async function addUsuarioEmpresa(
  empresa: AddEmpresaCommandProps
): Promise<AxiosResponse> {
  const resp = await axios.post(`/Usuario/empresa`, empresa)
  return resp
}
